import { useEffect, useRef } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';
import { isMfaHardEnforcementDisabled } from '../mfaEnforcement.js';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
const MFA_ENDPOINT_ALLOWLIST = ['/api/auth/mfa', '/api/auth/logout', '/api/mfa'];

const pathnameForRequestUrl = (url: string): string => {
  try {
    return new URL(url, globalThis.location?.origin ?? 'http://localhost').pathname;
  } catch {
    return url;
  }
};

const isAllowedMfaEndpoint = (url: string): boolean => {
  const pathname = pathnameForRequestUrl(url);
  return MFA_ENDPOINT_ALLOWLIST.some(
    (allowed) => pathname === allowed || pathname.startsWith(`${allowed}/`),
  );
};

export const useMfaRequestGuard = () => {
  const installedRef = useRef(false);

  useEffect(() => {
    if (installedRef.current) {
      return;
    }

    const testEnforce =
      (globalThis as { __ENABLE_MFA_ENFORCEMENT_IN_TESTS__?: boolean })
        .__ENABLE_MFA_ENFORCEMENT_IN_TESTS__ === true;

    // Skip fetch guarding in test environments to avoid rejecting test fetches
    // when MFA state is not bootstrapped.
    if (
      (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.[
        'NODE_ENV'
      ] === 'test' &&
      !testEnforce
    ) {
      return;
    }

    const originalFetch = globalThis.fetch?.bind(globalThis);
    if (typeof originalFetch !== 'function') {
      return;
    }

    if (isMfaHardEnforcementDisabled()) {
      return;
    }

    const guardedFetch: typeof fetch = async (input, init) => {
      const methodFromInput =
        typeof input === 'object' && input !== null && 'method' in input
          ? (input as Request).method
          : undefined;
      const method = (methodFromInput ?? init?.method ?? 'GET').toUpperCase();

      if (MUTATING_METHODS.has(method) && !isMfaHardEnforcementDisabled()) {
        const url =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.toString()
              : (input.url ?? '');
        const { mfaEnrolled, mfaSessionVerified } = useGlobalStore.getState();
        const requiresMfaSetup = mfaEnrolled === false;
        const requiresSessionVerification = mfaEnrolled === true && !mfaSessionVerified;
        if (!isAllowedMfaEndpoint(url) && (requiresMfaSetup || requiresSessionVerification)) {
          return Promise.resolve(new Response(null, { status: 403, statusText: 'mfa-required' }));
        }
      }

      return originalFetch(input as RequestInfo | URL, init);
    };

    globalThis.fetch = guardedFetch;
    installedRef.current = true;

    return () => {
      if (installedRef.current && typeof originalFetch === 'function') {
        globalThis.fetch = originalFetch;
        installedRef.current = false;
      }
    };
  }, []);
};
